import { Locale } from '@tuic/i18n';
import { expandString } from '@tuic/web-compositions/utils/i18n';
import { ContentfulBlocks, ContentfulDocument, ContentfulInlines, documentToReactTree } from '@tuic/contentful';
import { ReactNode } from 'react';
import { NetworkAccessType } from '@modules/get-slash-internet-card/lib/use-network-access-type';
import { GetSlashInternetSession } from '@modules/get-slash-internet-card/lib/use-get-slash-internet';
import { ConnectionStatus } from '@modules/get-slash-internet-card/components/connection-status-symbol';
import { CombinedInternetTariff } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-listing';
import { twJoin } from '@tuic/tailwind-config';
import { Dialog, DialogContent, DialogTrigger } from '@tuic/web-compositions/shared/Dialog';

/**
 * Function type for rendering text with placeholders
 * @param text - The text to render, which may contain placeholders
 * @returns The rendered text with placeholders replaced, or undefined if input is undefined
 */
export type TextRenderer = (text: string | undefined) => string | undefined;

/**
 * Function type for rendering Contentful documents with placeholders
 * @param doc - The Contentful document to render
 * @returns React node with the rendered document content
 */
export type DocumentRenderer = (doc: ContentfulDocument | undefined) => ReactNode;

/**
 * Formats a tariff price as a localized currency string.
 *
 * @param locale - The current locale for formatting
 * @param tariff - The tariff containing price information
 * @returns Formatted price string or undefined if price is not available
 */
export const getPriceExpression = (locale: Locale, tariff: CombinedInternetTariff): string | undefined => {
  const price = tariff.getSlash.price;
  if (typeof price === 'undefined') {
    return undefined;
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: tariff.getSlash.currency,
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(price);
};

/**
 * Creates a function that renders text with placeholders.
 * The returned function replaces placeholders in text with values from the provided record.
 *
 * @param placeholders - Record of placeholder keys and their replacement values
 * @returns A TextRenderer function that replaces placeholders in text
 */
export const createTextRenderer = (placeholders: Record<string, string | undefined> | undefined): TextRenderer => {
  return text => {
    if (typeof text === 'undefined') {
      return undefined;
    }
    try {
      return expandString(text, placeholders);
    } catch (e) {
      return text;
    }
  };
};

/**
 * Creates a function that renders Contentful documents with placeholders.
 * The returned function converts a Contentful document to React nodes,
 * replacing any placeholders in the text with values from the provided record.
 *
 * @param placeholders - Record of placeholder keys and their replacement values
 * @returns A DocumentRenderer function that renders Contentful documents with placeholders replaced
 */
export const createDocumentRenderer = (placeholders: Record<string, string | undefined> | undefined): DocumentRenderer => {
  return (doc: ContentfulDocument | undefined) => {
    return documentToReactTree(doc, children => <div className="cf-rich-text">{children}</div>, {
      renderText: text => {
        try {
          return expandString(text, placeholders);
        } catch (e) {
          return text;
        }
      },
      renderNode: {
        [ContentfulBlocks.OL_LIST]: (node, children) => <ol className="no-cf-rich-text list-none pl-0 [counter-reset:item] space-y-4">
              {children}
            </ol>,
        [ContentfulBlocks.LIST_ITEM]: (node, children) => <li className="flex gap-3 [counter-increment:item] items-start [&&&&]:ml-0">
              <span className={twJoin('mt-2 flex-none flex items-center justify-center size-6 rounded-full font-semibold text-xs text-white before:content-[counter(item)] before:leading-none', 'bg-secondary-marine-100')}></span>
              <span className="flex-1">{children}</span>
            </li>
      }
    });
  };
};
/**
 * Determines the current connection status based on network access type and session state.
 *
 * @param networkAccessType - The current network access type
 * @param session - The current internet session information
 * @returns The appropriate connection status for display
 */
export const getConnectionStatus = (networkAccessType: NetworkAccessType | undefined, session: GetSlashInternetSession | undefined): ConnectionStatus => {
  if (typeof networkAccessType === 'undefined') {
    return ConnectionStatus.Unknown;
  }
  if (networkAccessType !== NetworkAccessType.Ship) {
    return ConnectionStatus.NoConnection;
  }
  if (typeof session === 'undefined') {
    return ConnectionStatus.Unknown;
  }
  if (!session.hasBookedTariff) {
    return ConnectionStatus.Connected;
  }
  if (session.remainingVolume === 0) {
    return ConnectionStatus.Exhausted;
  }
  if (session.remainingTime === 0) {
    return ConnectionStatus.Exhausted;
  }
  return session.active ? ConnectionStatus.Active : ConnectionStatus.Paused;
};
export const renderTermsOfUseCheckboxText = (text: ContentfulDocument) => {
  return documentToReactTree(text, undefined, {
    renderNode: {
      [ContentfulInlines.HYPERLINK]: (node, children) => {
        const url = node.data.uri;

        // We assume that children is just a single text node
        const content = `${children}`;
        return <Dialog>
            <DialogTrigger asChild>
              <a className="underline">
                {content}
              </a>
            </DialogTrigger>
            <DialogContent fullscreen disableSpacing title={content}>
               <iframe id="internetTariffToS" src={url} title={content} className="w-full h-full" />
            </DialogContent>
          </Dialog>;
      }
    }
  });
};